import { Component, EventEmitter, Input, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { RgbColor } from '../color-picker.component';

@Component({
  selector: 'app-hue-slider',
  templateUrl: './hue-slider.component.html',
  styleUrls: ['./hue-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HueSliderComponent {
  public maxValue = 360;
  public minValue = 0;
  public colorAngleValue: number;

  private _rgbColor: RgbColor;

  @Input() set rgbColor(rgbColor: RgbColor) {
    this._rgbColor = rgbColor;
    this.convertRgbToAngleValue(this._rgbColor);
  }

  @Output() hueUpdated: EventEmitter<RgbColor> = new EventEmitter();

  constructor() {}

  private convertRgbToAngleValue(rgbColor: RgbColor) {
    if (!rgbColor) {
      return;
    }
    if (rgbColor.r === rgbColor.g && rgbColor.r === rgbColor.b) {
      this.colorAngleValue = 0;
      return;
    }
    // Find dominant two colors
    let colorAngleValue = 0;
    // If red and green are dominant
    if (rgbColor.r >= rgbColor.b && rgbColor.g >= rgbColor.b) {
      const proportionRed = rgbColor.r / (rgbColor.r + rgbColor.g);
      colorAngleValue = 120 - Math.round(proportionRed * 120);
    }
    // If green and blue are dominant
    if (rgbColor.g >= rgbColor.r && rgbColor.b >= rgbColor.r) {
      const proportionGreen = rgbColor.g / (rgbColor.g + rgbColor.b);
      colorAngleValue = 240 - Math.round(proportionGreen * 120);
    }
    // If blue and red are dominant
    if (rgbColor.r >= rgbColor.g && rgbColor.b >= rgbColor.g) {
      const proportionBlue = rgbColor.b / (rgbColor.r + rgbColor.b);
      colorAngleValue = 360 - Math.round(proportionBlue * 120);
    }
    if (colorAngleValue === 360 && this.colorAngleValue === 0) {
      this.colorAngleValue = 0;
    } else {
      this.colorAngleValue = colorAngleValue;
    }
  }

  public emitHueChange() {
    const rgbColor = this.parseGradesToRgb(this.colorAngleValue);
    this.hueUpdated.emit(rgbColor);
  }

  private parseGradesToRgb(value: number) {
    // 0 or 360 > only red, 120 > only green, 240 only blue
    if (value === 0 || value === 360) {
      return new RgbColor(255, 0, 0);
    }
    if (value === 60) {
      return new RgbColor(255, 255, 0);
    }
    if (value === 120) {
      return new RgbColor(0, 255, 0);
    }
    if (value === 180) {
      return new RgbColor(0, 255, 255);
    }
    if (value === 240) {
      return new RgbColor(0, 0, 255);
    }
    if (value === 300) {
      return new RgbColor(255, 0, 255);
    }
    // If it's a mix of red and green
    if (value > 0 && value < 60) {
      const green = Math.round((value / 60) * 255);
      return new RgbColor(255, green, 0);
    }
    if (value > 60 && value < 120) {
      const red = 255 - Math.round(((value - 60) / 60) * 255);
      return new RgbColor(red, 255, 0);
    }

    if (value > 120 && value < 180) {
      const blue = Math.round(((value - 120) / 60) * 255);
      return new RgbColor(0, 255, blue);
    }
    if (value > 180 && value < 240) {
      const green = 255 - Math.round(((value - 180) / 60) * 255);
      return new RgbColor(0, green, 255);
    }

    if (value > 240 && value < 300) {
      const red = Math.round(((value - 240) / 60) * 255);
      return new RgbColor(red, 0, 255);
    }
    if (value > 300 && value < 360) {
      const blue = 255 - Math.round(((value - 300) / 60) * 255);
      return new RgbColor(255, 0, blue);
    }
    throw new Error('Should never get here with value: ' + value);
  }
}
